import { Component, OnInit, ViewChild, InjectionToken, Optional, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ApiService, DossierDto, MedecinDto, PatientDto, DemandeurDto } from '../../core/services/api.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ITypesSelection } from 'src/app/core/interfaces/types-selection.interface';
import { TexteService } from 'src/app/core/services/texte.service';
import { MedecinFormComponent } from './medecin-form/medecin-form.component';
import { PatientFormComponent } from './patient-form/patient-form.component';
import { DemandeFormComponent } from './demande-form/demande-form.component';
import { ContactFormComponent } from './contact-form/contact-form.component';
import { DemandeurFormComponent } from './demandeur-form/demandeur-form.component';
import { RendezVousFormComponent } from './rendez-vous-form/rendez-vous-form.component';
import { HistoriqueFormComponent } from './historique-form/historique-form.component';
import { AllergieFormComponent } from './allergie-form/allergie-form.component';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';

export const RECAPTCHA_KEY = new InjectionToken<string>('RECAPTCHA_KEY');

@Component({
    selector: 'app-formulaire',
    templateUrl: './formulaire.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormulaireComponent implements OnInit, OnDestroy {

  private subscription: Subscription;
  public idLangue: number;
  public typesSelected: ITypesSelection | null = null;
  public indicatifs: number[];
  public recaptchaValid: boolean;
  public recaptchaKey: string;
  public displayConfirmation: boolean;
  public displayError: boolean;
  public rgpd: boolean;

  @ViewChild('MedecinFormMed', { static: false }) MedecinFormMed: MedecinFormComponent;
  @ViewChild('PatientForm', { static: false }) PatientForm: PatientFormComponent;
  @ViewChild('DemandeurForm', { static: false }) DemandeurForm: DemandeurFormComponent;
  @ViewChild('ContactForm', { static: false }) ContactForm: ContactFormComponent;
  @ViewChild('MedecinFormInt', { static: false }) MedecinFormInt: MedecinFormComponent;
  @ViewChild('DemandeForm', { static: false }) DemandeForm: DemandeFormComponent;
  @ViewChild('RendezvousForm', { static: false }) RendezvousForm: RendezVousFormComponent;
  @ViewChild('HistoriqueForm', { static: false }) HistoriqueForm: HistoriqueFormComponent;
  @ViewChild('AllergieForm', { static: false }) AllergieForm: AllergieFormComponent;

  constructor(
    private apiService: ApiService,
    public texteService: TexteService,
    private spinner: NgxSpinnerService,
    private router: Router,
    @Optional() @Inject(RECAPTCHA_KEY) recaptchaKey?: string
  ) {
    this.recaptchaKey = recaptchaKey ? recaptchaKey : '';
  }

  ngOnInit(): void {
    this.indicatifs = [33];
    this.recaptchaValid = false;
    this.displayConfirmation = false;
    this.displayError = false;
    this.rgpd = false;

    this.subscription = this.texteService.Langue.subscribe(value => {
      if (value != null) {
        this.idLangue = value.id;
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onTypesSelectionChange(event: ITypesSelection | null) {
    this.typesSelected = event;
  }

  recaptchaResolved(captchaResponse: string) {
    this.apiService.validUserTokenGoogle(captchaResponse).subscribe(response => {
      this.recaptchaValid = response;
    });
  }

  public onIndicatifsChange(event) {
    if (event != null && event.length > 0) {
      this.indicatifs = event;
    }
  }

  isValidForm(): boolean {
    if (this.typesSelected == null || this.typesSelected.situation == null) { return false; }
    if (this.PatientForm == null || !this.PatientForm.isValid()) { return false; }
    if (this.typesSelected.situation.code === 'FRA') {
      if (this.ContactForm == null || !this.ContactForm.isValid()) { return false; }
      if (this.typesSelected.demande != null) {
        if (this.DemandeurForm == null || !this.DemandeurForm.isValid()) { return false; }
        if (this.DemandeForm == null || !this.DemandeForm.isValid()) { return false; }
      } else {
        if (this.RendezvousForm == null || !this.RendezvousForm.isValid()) { return false; }
      }
    } else if (this.typesSelected.situation.code === 'INT') {
      if (this.DemandeurForm == null || !this.DemandeurForm.isValid()) { return false; }
      if (this.ContactForm == null || !this.ContactForm.isValid()) { return false; }
      if (this.DemandeForm == null || !this.DemandeForm.isValid()) { return false; }
      if (this.MedecinFormInt == null || !this.MedecinFormInt.isValid()) { return false; }
      if (this.HistoriqueForm == null || !this.HistoriqueForm.isValid()) { return false; }
      if (this.AllergieForm == null || !this.AllergieForm.isValid()) { return false; }

    } else if (this.typesSelected.situation.code === 'MED') {
      if (this.MedecinFormMed == null || !this.MedecinFormMed.isValid()) { return false; }
      if (this.DemandeForm == null || !this.DemandeForm.isValid()) { return false; }
    }
    return true;
  }

  onSubmit() {
    this.displayError = false;

    if (this.isValidForm()) {
      this.spinner.show();

      const dossier = new DossierDto();
      dossier.idLangue = this.idLangue != null ? this.idLangue : null;
      dossier.typeSituation = this.typesSelected.situation;
      dossier.typeDemande = this.typesSelected.demande;
      dossier.typeCouverture = this.typesSelected.couverture;

      dossier.patient = new PatientDto(this.PatientForm.getForm());
      if (this.typesSelected.situation.code === 'FRA') {
        const contact = this.ContactForm.getForm();
        dossier.patient.telephone = contact.telephone;
        dossier.patient.telephone2 = contact.telephone2;
        dossier.patient.email = contact.email;
        dossier.patient.isContactMatin = contact.isContactMatin;
        if (this.typesSelected.demande != null && this.typesSelected.demande.code === 'NOU') {
          dossier.typeDemande = this.typesSelected.demande;
          dossier.demandeur = new DemandeurDto(this.DemandeurForm.getForm());

          const demande = this.DemandeForm.getForm();
          dossier.codeSpecialite = demande.specialite;
          dossier.codePathologie = demande.pathologie;
          dossier.patient.cancer = demande.cancer;
          dossier.information = demande.information;
        } else {
          const rendezvous = this.RendezvousForm.getForm();
          dossier.codeSpecialite = rendezvous.specialite;
          dossier.codePathologie = rendezvous.pathologie;
          dossier.typeDemande = rendezvous.demande;
          dossier.dateRendezVous = rendezvous.dateRendezvous;
        }

      } else if (this.typesSelected.situation.code === 'INT') {
        dossier.demandeur = new DemandeurDto(this.DemandeurForm.getForm());

        const contact = this.ContactForm.getForm();
        dossier.patient.telephone = contact.telephone;
        dossier.patient.telephone2 = contact.telephone2;
        dossier.patient.email = contact.email;
        dossier.patient.isContactMatin = contact.isContactMatin;

        const demande = this.DemandeForm.getForm();
        dossier.codeSpecialite = demande.specialite;
        dossier.codePathologie = demande.pathologie;
        dossier.patient.cancer = demande.cancer;
        dossier.information = demande.information;
        dossier.medecin = new MedecinDto(this.MedecinFormInt.getForm());

        const historique = this.HistoriqueForm.getForm();
        dossier.patient.taille = historique.taille;
        dossier.patient.poid = historique.poid;
        dossier.patient.fume = historique.fume;
        dossier.patient.alcool = historique.alcool;
        dossier.patient.conditions = historique.conditions;
        dossier.patient.infection = historique.infection;

        const allergie = this.AllergieForm.getForm();
        dossier.patient.allergie = allergie.allergie;
        dossier.patient.precision = allergie.precision;
        dossier.patient.listeMedicament = allergie.listeMedicament;

      } else if (this.typesSelected.situation.code === 'MED') {
        dossier.medecin = new MedecinDto(this.MedecinFormMed.getForm());
        const demande = this.DemandeForm.getForm();
        dossier.codeSpecialite = demande.specialite;
        dossier.codePathologie = demande.pathologie;
        dossier.patient.cancer = demande.cancer;
        dossier.information = demande.information;
      }

      this.apiService.setDossier(dossier).subscribe(res => {
        if (!(this.typesSelected.situation.code === 'FRA' && this.typesSelected.demande == null)) {
          this.DemandeForm.uploadFiles(res.value).subscribe(data => {
            if (data) {
              this.sendMail(res.value);
            } else {
              this.displayError = true;
              this.spinner.hide();
            }
          });
        } else {
          this.sendMail(res.value);
        }
      },
        err => {
          console.log(err);
          this.displayError = true;
          this.spinner.hide();
        });
    }
  }

  sendMail(idDossier: number) {
    this.apiService.sendMail(idDossier).subscribe(
      () => {
        this.displayConfirmation = true;
        this.spinner.hide();
      },
      err => {
        console.log(err);
        this.displayError = true;
        this.spinner.hide();
      }
    );
  }

  backToHome() {
    window.location.href = 'https://www.gustaveroussy.fr/';
  }

  goToMentions() {
    this.router.navigate(['mentions-legales']);
  }
}
